import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  Mesh,
  Texture,
  Object3D,
  Group,
  GammaEncoding,
  AnimationMixer,
  TextureLoader,
  DirectionalLight,
  AmbientLight,
  PMREMGenerator,
} from 'three';
import { GLTFLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { Main } from './main';

import envAsset from './assets/images/env.jpg';
import glbAsset from './assets/models/lnx.glb';
import { ArilynLibrary } from './ALEvents';

export class ThreeScene {
  private readonly modelLoaded = new ArilynLibrary.ALEvent<number>();
  public get ModelLoadedEvent() {
    return this.modelLoaded.expose();
  }

  main: Main;
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  gltfParent?: Group;

  private mixer?: AnimationMixer;
  private environment?: Texture;

  private lastUpdate: number = performance.now();
  private now: number = performance.now();
  private dt: number = 0;

  constructor(main: Main) {
    this.main = main;
    this.scene = new Scene();
    this.camera = new PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.01, 1000);
    this.scene.add(this.camera);

    let ambient = new AmbientLight(0xffffff, 0.8);
    this.scene.add(ambient);

    this.renderer = this.createRenderer(false, window.devicePixelRatio);

    let light = new DirectionalLight(0xffffff, 0.2);
    light.position.set(2, 10, -2);
    light.target.position.set(0, 0, 0);
    this.scene.add(light);

    window.addEventListener('resize', this.onWindowResize.bind(this), false);
  }

  load() {
    this.loadEnvironment();
  }

  update() {
    this.now = performance.now();
    this.dt = (this.now - this.lastUpdate) * 0.001;
    this.lastUpdate = this.now;
    if (this.mixer) this.mixer.update(this.dt);
    this.renderer.render(this.scene, this.camera);
  }

  private createRenderer(AA: boolean, pixelRatio: number): WebGLRenderer {
    let rend = new WebGLRenderer({ alpha: true, antialias: AA });
    rend.setPixelRatio(pixelRatio);
    rend.setSize(window.innerWidth, window.innerHeight);
    rend.setClearColor(0x000000, 0);
    rend.outputEncoding = GammaEncoding;
    return rend;
  }

  private loadGLTF() {
    if (this.gltfParent) {
      this.modelLoaded.invoke();
    } else {
      let loader = new GLTFLoader();
      loader.load(glbAsset, (gltf: GLTF) => {
        this.gltfParent = new Group();
        this.traverseChildren(gltf.scene);
        this.configureAnimations(gltf);
        gltf.scene.position.set(0, 0, 0);
        this.gltfParent.position.set(0, 0, 0);
        this.gltfParent.add(gltf.scene);
        this.scene.add(this.gltfParent);
        this.modelLoaded.invoke();
      });
    }
  }

  private configureAnimations(gltf: GLTF) {
    this.mixer = new AnimationMixer(gltf.scene);
    gltf.animations.forEach((clip) => {
      if (this.mixer) {
        let action = this.mixer.clipAction(clip);
        action.clampWhenFinished = true;
        action.play();
      }
    });
  }

  private loadEnvironment() {
    if (this.environment) {
      this.loadGLTF();
    } else {
      let pmremGenerator = new PMREMGenerator(this.renderer);
      pmremGenerator.compileEquirectangularShader();
      let loader = new TextureLoader();
      loader.load(envAsset, (texture) => {
        this.environment = pmremGenerator.fromEquirectangular(texture).texture;
        this.loadGLTF();
      });
    }
  }

  private traverseChildren(mesh: Object3D) {
    mesh.children.forEach((child) => {
      if (child instanceof Mesh) {
        if (child.material) {
          child.material.envMap = this.environment;
          child.material.needsUpdate = true;
          child.frustumCulled = false;
        }
      }
      if (child.children.length > 0) {
        this.traverseChildren(child);
      }
    });
  }

  private onWindowResize() {
    let w = window.innerWidth;
    let h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(w, h);
  }
}
